/**
 * The map, with the colored dots.
 *
 * Kate: "on the map you can see little dots. So like if you're a whatever player you
 * are, it'll say below your thing what color dot you are... Other people, not yourself,
 * but other people will see a blue dot so you can come walk and find them."
 *
 * Two rules fall out of that, and both are load-bearing:
 *   1. You do NOT see your own dot — only other players' dots.
 *   2. Your own colour is shown under your nameplate, so you know what others see.
 *
 * This is what replaced teleporting. She removed teleport-to-player and gave the dots
 * as the way to find each other, so the map is the entire find-your-friends mechanic.
 */
import { MAP_HALF_EXTENT } from '../world/map'

/** Colours handed out to players, in order. Seven, because seven can play. */
export const PLAYER_COLORS = [
  '#4a9df8', // blue — her example
  '#f2544b',
  '#4fc76b',
  '#f2c14e',
  '#b06bf0',
  '#f08a3c',
  '#3fd0c9',
]

export interface MapDot {
  id: string
  x: number
  z: number
  color: string
}

const SIZE = 148

export class Minimap {
  readonly root: HTMLDivElement
  private readonly canvas: HTMLCanvasElement
  private readonly context: CanvasRenderingContext2D
  private readonly swatch: HTMLSpanElement
  private readonly label: HTMLDivElement

  constructor(parent: HTMLElement) {
    this.root = document.createElement('div')
    this.root.className = 'minimap'

    this.canvas = document.createElement('canvas')
    const dpr = Math.min(window.devicePixelRatio, 2)
    this.canvas.width = SIZE * dpr
    this.canvas.height = SIZE * dpr
    this.canvas.style.width = `${SIZE}px`
    this.canvas.style.height = `${SIZE}px`
    const context = this.canvas.getContext('2d')
    if (!context) throw new Error('2d context unavailable')
    context.scale(dpr, dpr)
    this.context = context
    this.root.append(this.canvas)

    // "It'll say below your thing what colour dot you are."
    this.label = document.createElement('div')
    this.label.className = 'minimap-you'
    this.swatch = document.createElement('span')
    this.swatch.className = 'minimap-swatch'
    const text = document.createElement('span')
    text.textContent = 'your dot'
    this.label.append(this.swatch, text)
    this.root.append(this.label)

    parent.append(this.root)
  }

  /** Set the colour others see for this player. */
  setOwnColor(color: string): void {
    this.swatch.style.background = color
  }

  /**
   * Draw. `others` must not include the local player — Kate was explicit that you don't
   * see your own dot.
   */
  update(selfX: number, selfZ: number, selfYaw: number, others: readonly MapDot[]): void {
    const ctx = this.context
    ctx.clearRect(0, 0, SIZE, SIZE)

    ctx.fillStyle = 'rgba(8, 18, 6, 0.62)'
    ctx.fillRect(0, 0, SIZE, SIZE)
    ctx.strokeStyle = 'rgba(200, 226, 190, 0.5)'
    ctx.lineWidth = 1
    ctx.strokeRect(0.5, 0.5, SIZE - 1, SIZE - 1)

    const toMap = (x: number, z: number): [number, number] => [
      ((x + MAP_HALF_EXTENT) / (MAP_HALF_EXTENT * 2)) * SIZE,
      ((z + MAP_HALF_EXTENT) / (MAP_HALF_EXTENT * 2)) * SIZE,
    ]

    for (const dot of others) {
      const [px, py] = toMap(dot.x, dot.z)
      ctx.beginPath()
      ctx.arc(px, py, 4, 0, Math.PI * 2)
      ctx.fillStyle = dot.color
      ctx.fill()
      ctx.strokeStyle = 'rgba(0, 0, 0, 0.55)'
      ctx.stroke()
    }

    // The player is drawn as a facing arrow, not a coloured dot — their own dot is
    // deliberately absent, so the arrow only says "you are here, pointing this way".
    const [sx, sy] = toMap(selfX, selfZ)
    ctx.save()
    ctx.translate(sx, sy)
    ctx.rotate(-selfYaw)
    ctx.beginPath()
    ctx.moveTo(0, -6)
    ctx.lineTo(4.5, 5)
    ctx.lineTo(0, 2.5)
    ctx.lineTo(-4.5, 5)
    ctx.closePath()
    ctx.fillStyle = '#ffffff'
    ctx.fill()
    ctx.restore()
  }
}
